import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  UserCircle,
  ClipboardText,
  Truck,
  Archive,
  CurrencyDollar,
  Users,
  Cube,
  Eye,
  EyeSlash,
  ArrowLeft,
  Package,
  ArrowRight,
  FileText
} from '@phosphor-icons/react';

const ROLE_OPTIONS = [
  { 
    id: 'purchasing_manager', 
    label: 'Purchasing Manager', 
    icon: Users,
    description: 'Full procurement oversight',
    actions: ['View & Create POs', 'Manage Pipeline'],
    demoEmail: 'demo@herchainsolutions.com',
    demoPassword: 'e2S0qu56c!Uw5qBPd3HD',
    demoName: 'Demo Purchasing Manager',
    color: 'from-[#3D1D72] to-[#5B2D9E]'
  },
  { 
    id: 'buyer', 
    label: 'Buyer', 
    icon: ClipboardText,
    description: 'Create purchase requisitions',
    actions: ['Submit Requisitions', 'Track Status'],
    demoEmail: 'buyer@herchainsolutions.com',
    demoPassword: 'Zv_XvAqyoEpHuFLYX-Na',
    demoName: 'Demo Buyer',
    color: 'from-[#F59E0B] to-[#FBBF24]'
  },
  { 
    id: 'department_head', 
    label: 'Department Head', 
    icon: UserCircle,
    description: 'Approve department requests',
    actions: ['Approve Requisitions', 'View Spend'],
    demoEmail: 'depthead@herchainsolutions.com',
    demoPassword: 'feCgO2MLyddT5rFa9f_7',
    demoName: 'Demo Department Head',
    color: 'from-[#0F52BA] to-[#3B82F6]'
  },
  { 
    id: 'financial_controller', 
    label: 'Financial Controller', 
    icon: CurrencyDollar,
    description: 'Financial approvals',
    actions: ['Approve POs', 'Process Payments'],
    demoEmail: 'fc@herchainsolutions.com',
    demoPassword: 'f%!P%RZw5_G-Cir7JS1o',
    demoName: 'Demo Financial Controller',
    color: 'from-[#059669] to-[#10B981]'
  },
  { 
    id: 'vendor', 
    label: 'Vendor', 
    icon: Package,
    description: 'Supplier portal access',
    actions: ['Confirm POs', 'Upload Documents'],
    demoEmail: 'vendor@herchainsolutions.com',
    demoPassword: '7fdPtWt36b3Typ-WuYcY',
    demoName: 'Atlantic Food Suppliers',
    color: 'from-[#7C3AED] to-[#A78BFA]'
  },
  { 
    id: 'freight_forwarder', 
    label: 'Freight Forwarder', 
    icon: Truck,
    description: 'Shipping & logistics',
    actions: ['Update Shipments', 'Track Cargo'],
    demoEmail: 'freight@herchainsolutions.com',
    demoPassword: 'p!4+8uGWp%x%PcHpRloG',
    demoName: 'Caribbean Cargo Services',
    color: 'from-[#0891B2] to-[#22D3EE]'
  },
  { 
    id: 'receiver', 
    label: 'Receiver', 
    icon: Archive,
    description: 'Goods receiving',
    actions: ['Receive Goods', 'Record Variance'],
    demoEmail: 'receiver@herchainsolutions.com',
    demoPassword: 'DPFr9K5ggnhHEGRyVKP8',
    demoName: 'Demo Receiver',
    color: 'from-[#14B8A6] to-[#2DD4BF]'
  },
  { 
    id: 'storekeeper', 
    label: 'Storekeeper', 
    icon: Cube,
    description: 'Inventory management',
    actions: ['Manage Stock', 'Issue Items'],
    demoEmail: 'storekeeper@herchainsolutions.com',
    demoPassword: '54N+A-MjPzDn2Eo1n60q',
    demoName: 'Demo Storekeeper',
    color: 'from-[#8B5CF6] to-[#A78BFA]'
  },
  { 
    id: 'internal_requester', 
    label: 'Internal Requester', 
    icon: ClipboardText,
    description: 'Department requests',
    actions: ['Request Items', 'Track Requests'],
    demoEmail: 'requester@herchainsolutions.com',
    demoPassword: '2oG6ccsHZ2rzcf6bO5m-',
    demoName: 'Internal Requester',
    color: 'from-[#EF4444] to-[#F87171]'
  },
  {
    id: 'accounts_payable',
    label: 'Accounts Payable',
    icon: FileText,
    description: 'Closed POs & AP packages',
    actions: ['View Closed POs', 'Export AP Package'],
    demoEmail: 'ap@herchainsolutions.com',
    demoPassword: 'OL3jfKc%!xv_7TabetvV',
    demoName: 'Accounts Payable',
    color: 'from-[#0891B2] to-[#22D3EE]'
  }
];

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, logout } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);

  // Auto-logout when visiting /demo page to allow role switching
  useEffect(() => {
    if (user) {
      logout();
    }
    // Pre-select a role if ?role=... is provided (used by Pilot Workflow widget)
    try {
      const params = new URLSearchParams(location.search);
      const roleParam = (params.get('role') || '').toLowerCase();
      if (roleParam) {
        const match = ROLE_OPTIONS.find(r => r.id === roleParam);
        if (match) {
          setSelectedRole(match.id);
          setEmail(match.demoEmail);
          setPassword(match.demoPassword);
        }
      }
    } catch (_) { /* noop */ }
  }, []); // Only run once on mount

  const redirectByRole = (role) => {
    switch (role) {
      case 'vendor':
        navigate('/vendor-portal');
        break;
      case 'freight_forwarder':
        navigate('/freight-portal');
        break;
      case 'buyer':
        navigate('/buyer-portal');
        break;
      case 'department_head':
        navigate('/dh-portal');
        break;
      case 'receiver':
        navigate('/receiver-portal');
        break;
      case 'accounts_payable':
        navigate('/receiver-portal');
        break;
      case 'storekeeper':
        navigate('/storekeeper-portal');
        break;
      case 'financial_controller':
        navigate('/fc-approval');
        break;
      case 'internal_requester':
        navigate('/internal-requester-portal');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const selectDemoAccount = (role) => {
    setSelectedRole(role.id);
    setEmail(role.demoEmail);
    setPassword(role.demoPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      toast.success('Login successful!');
      redirectByRole(userData.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Top nav row: Back to Landing + Sign in to your account */}
      <div className="p-4 sm:p-6 flex items-center justify-between gap-3">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3D1D72] transition-colors"
          data-testid="back-to-landing"
        >
          <ArrowLeft size={20} />
          Back to Home
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#3D1D72] transition-colors"
          data-testid="demo-to-signin-link"
        >
          Sign in to your account
          <ArrowRight size={14} />
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center px-4 pb-8">
        <div className="w-full max-w-5xl">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] shadow-xl mb-3">
              <span className="text-white font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
            </div>
            <h1 className="text-2xl font-bold text-[#3D1D72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              HERChain Solutions Demo
            </h1>
            <p className="text-slate-500 text-sm mt-1">Select a role to explore the platform</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Role Cards - Compact Grid */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-3 gap-3">
                {ROLE_OPTIONS.map((role) => {
                  const Icon = role.icon;
                  const isSelected = selectedRole === role.id;
                  return (
                    <button
                      key={role.id}
                      onClick={() => selectDemoAccount(role)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-[#3D1D72] bg-[#3D1D72]/5 shadow-lg scale-[1.02]' 
                          : 'border-slate-200 hover:border-slate-300 bg-white hover:shadow-md'
                      }`}
                      data-testid={`role-${role.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${role.color} flex items-center justify-center mb-2`}>
                        <Icon size={18} weight="fill" className="text-white" />
                      </div>
                      <p className="font-semibold text-slate-800 text-sm leading-tight">{role.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{role.description}</p>
                      
                      {/* Key Actions */}
                      <div className="mt-2 space-y-1">
                        {role.actions.map((action, idx) => (
                          <div key={idx} className="flex items-center gap-1 text-xs text-slate-600">
                            <ArrowRight size={10} className="text-[#3D1D72]" />
                            <span>{action}</span>
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Login Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sticky top-4">
                {selectedRole ? (
                  <>
                    <div className="text-center mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ROLE_OPTIONS.find(r => r.id === selectedRole)?.color} flex items-center justify-center mx-auto mb-2`}>
                        {(() => {
                          const role = ROLE_OPTIONS.find(r => r.id === selectedRole);
                          const Icon = role?.icon;
                          return Icon ? <Icon size={24} weight="fill" className="text-white" /> : null;
                        })()}
                      </div>
                      <p className="font-semibold text-slate-800">
                        {ROLE_OPTIONS.find(r => r.id === selectedRole)?.label}
                      </p>
                      <p className="text-xs text-slate-500">
                        {ROLE_OPTIONS.find(r => r.id === selectedRole)?.demoName}
                      </p>
                    </div>
                    
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="text-sm"
                          data-testid="login-email-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm">Password</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="pr-10 text-sm"
                            data-testid="login-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-[#3D1D72] hover:bg-[#2D1656]"
                        disabled={loading}
                        data-testid="login-btn"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Signing in...
                          </span>
                        ) : (
                          'Sign In'
                        )}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <UserCircle size={32} className="text-slate-400" />
                    </div>
                    <p className="font-medium text-slate-700">Select a Role</p>
                    <p className="text-sm text-slate-500 mt-1">Click any role card to see their portal</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
